import React from 'react';
import { Link } from 'react-router-dom';
import { Search, HelpCircle, Book, Shield, CreditCard, UserCheck, AlertCircle, MessageCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Help = () => {
  const categories = [
    { icon: UserCheck, title: "Getting Started", desc: "Account creation, verification, and profile setup", link: "/faq" },
    { icon: Book, title: "Booking Guide", desc: "How to book providers and manage requests", link: "/faq" },
    { icon: CreditCard, title: "Payments", desc: "Payment methods, fees, and transactions", link: "/faq" },
    { icon: Shield, title: "Safety", desc: "Safety features, SOS, and reporting", link: "/conduct" },
    { icon: HelpCircle, title: "FAQ", desc: "Frequently asked questions", link: "/faq" },
    { icon: MessageCircle, title: "Contact Support", desc: "Get help from our team", link: "/contact" }
  ];

  const quickHelp = [
    { q: "How do I verify my account?", a: "After registration, upload your ID proof (Aadhaar/PAN) in the Profile section. Verification takes 24-48 hours." },
    { q: "How do I become a provider?", a: "Purchase any membership plan from the Membership page. Once purchased, your provider profile is automatically activated." },
    { q: "How do I withdraw earnings?", a: "Go to Dashboard → Earnings → Withdraw. Enter amount (min ₹100), provide UPI/bank details, and submit." },
    { q: "What if I feel unsafe?", a: "Use the SOS button in your dashboard immediately. Our team will contact you and alert authorities if needed." },
    { q: "How do I report misconduct?", a: "Go to the booking details and click 'Report Issue'. Provide details and our team will investigate within 24 hours." }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <span className="sr-only-seo">human on rent help center, boyfriend on rent support, girlfriend on rent help</span>

      <Header />

      <main className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
              Help <span className="font-medium">Center</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Find answers, guides, and support to make the most of HumanOnRent.AI
            </p>
            
            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search for help..."
                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 bg-white text-lg focus:border-gray-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {categories.map((cat) => (
              <Link key={cat.title} to={cat.link} className="premium-card p-6 hover:border-gray-300">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4">
                  <cat.icon className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{cat.title}</h3>
                <p className="text-gray-600 text-sm">{cat.desc}</p>
              </Link>
            ))}
          </div>

          {/* Quick Help */}
          <div className="mb-16">
            <h2 className="text-2xl font-medium text-gray-900 mb-6">Quick Answers</h2>
            <div className="space-y-4">
              {quickHelp.map((item) => (
                <div key={item.q} className="premium-card p-6">
                  <h3 className="font-medium text-gray-900 mb-2">{item.q}</h3>
                  <p className="text-gray-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Emergency */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 mb-16">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-red-900 mb-2">Emergency Support</h3>
                <p className="text-red-800 mb-4">
                  If you're in an unsafe situation, use the SOS button in your dashboard immediately. 
                  For urgent matters, call our 24/7 emergency line.
                </p>
                <p className="text-red-900 font-semibold text-lg">Emergency: +91 98765 43210</p>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="text-center">
            <p className="text-gray-600 mb-4">Can't find what you're looking for?</p>
            <Link to="/contact" className="btn-premium inline-block">
              Contact Support
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Help;
